import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Chip,
  TextField,
} from '@mui/material';
import FilterChip from '../../common/filters/FilterChip';
import {
  BENCH_CANDIDATE_SOURCE_SYSTEM_OPTIONS,
  BENCH_CANDIDATE_SOURCE_OPTIONS,
  BENCH_CANDIDATE_SOURCE_LABELS,
  BENCH_CANDIDATE_SOURCE_COLORS,
  BENCH_CANDIDATE_STATUSES,
  BENCH_CANDIDATE_STATUS_OPTIONS,
  BENCH_CANDIDATE_STATUS_COLORS,
  BENCH_CANDIDATE_MATCH_STATUS_OPTIONS,
  BENCH_CANDIDATE_MATCH_STATUS_LABELS,
  BENCH_CANDIDATE_MATCH_STATUS_COLORS,
} from '../../../constants/benchCandidates';
import { FilterBar } from './styles';
import { getChipSx } from './chipStyles';

const renderChips = (labels, colors) => (selected) => {
  if (!selected || selected.length === 0) {
    return <em>Any</em>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => (
        <Chip key={value} label={labels[value] || value} size="small" sx={getChipSx(colors[value])} />
      ))}
    </Box>
  );
};

const toArray = (value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value);

const MultiSelect = ({ id, label, value, options, labels, colors, onChange, minWidth = 220 }) => (
  <FormControl size="small" sx={{ minWidth }}>
    <InputLabel id={id}>{label}</InputLabel>
    <Select
      labelId={id}
      multiple
      value={value || []}
      onChange={(event) => onChange(toArray(event.target.value))}
      input={<OutlinedInput label={label} />}
      renderValue={renderChips(labels, colors)}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          <Chip label={opt.label} size="small" sx={getChipSx(colors[opt.value])} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PALETTE_BY_TOKEN = {
  info:    { backgroundColor: '#DBEAFE', textColor: '#1E40AF', borderColor: '#1E40AF' },
  success: { backgroundColor: '#E6F4EA', textColor: '#1E8E3E', borderColor: '#1E8E3E' },
  default: { backgroundColor: '#F3F4F6', textColor: '#6B7280', borderColor: '#6B7280' },
};

const STATUS_CHIP_TITLES = {
  [BENCH_CANDIDATE_STATUSES.NEW]: 'Rows nobody has acted on yet',
  [BENCH_CANDIDATE_STATUSES.ASSIGNED]: 'Rows already assigned to a bench project from this list',
  [BENCH_CANDIDATE_STATUSES.DISMISSED]: 'Rows an operator dismissed',
  [BENCH_CANDIDATE_STATUSES.REMOVED_UPSTREAM]: 'Rows the feed stopped returning',
};

// Multi-select status row: each chip toggles one status in filters.status.
const STATUS_CHIPS = BENCH_CANDIDATE_STATUS_OPTIONS.map((opt) => ({
  value: opt.value,
  label: opt.label,
  title: STATUS_CHIP_TITLES[opt.value],
  ...(PALETTE_BY_TOKEN[BENCH_CANDIDATE_STATUS_COLORS[opt.value]] || PALETTE_BY_TOKEN.default),
}));

const ELIGIBLE_CHIP = {
  label: 'Assignable only',
  title: 'Only rows that pass every blocking eligibility rule',
  ...PALETTE_BY_TOKEN.info,
};

// Text inputs update the filters after a short pause, not on every keystroke, so a
// query goes out per word rather than per letter (each change refetches the list).
const DEBOUNCE_MS = 300;
const DebouncedTextField = ({ value, onCommit, ...props }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => {
    if (draft === (value ?? '')) {
      return undefined;
    }
    const handle = setTimeout(() => onCommit(draft), DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return <TextField {...props} value={draft} onChange={(event) => setDraft(event.target.value)} />;
};

const CandidatesFilterBar = ({ filters, onChange }) => {
  const set = (patch) => onChange(patch);

  return (
    <FilterBar>
      {/* Only one source system exists today (Americas); the selector appears once a second one is configured.
          filters.sourceSystem is still sent — the API requires it. */}
      {BENCH_CANDIDATE_SOURCE_SYSTEM_OPTIONS.length > 1 && (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="bc-source-system">Source system</InputLabel>
          <Select
            labelId="bc-source-system"
            label="Source system"
            value={filters.sourceSystem}
            onChange={(event) => set({ sourceSystem: event.target.value })}
          >
            {BENCH_CANDIDATE_SOURCE_SYSTEM_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <MultiSelect
        id="bc-bench-source"
        label="Reason"
        value={filters.benchSource}
        options={BENCH_CANDIDATE_SOURCE_OPTIONS}
        labels={BENCH_CANDIDATE_SOURCE_LABELS}
        colors={BENCH_CANDIDATE_SOURCE_COLORS}
        onChange={(benchSource) => set({ benchSource })}
        minWidth={260}
      />


      <MultiSelect
        id="bc-match"
        label="Profile"
        value={filters.matchStatus}
        options={BENCH_CANDIDATE_MATCH_STATUS_OPTIONS}
        labels={BENCH_CANDIDATE_MATCH_STATUS_LABELS}
        colors={BENCH_CANDIDATE_MATCH_STATUS_COLORS}
        onChange={(matchStatus) => set({ matchStatus })}
        minWidth={190}
      />

      <DebouncedTextField
        size="small"
        label="Client"
        value={filters.clientName}
        onCommit={(value) => set({ clientName: value })}
        sx={{ minWidth: 160 }}
      />

      <DebouncedTextField
        size="small"
        label="Representative"
        value={filters.deliveryManagerName}
        onCommit={(value) => set({ deliveryManagerName: value })}
        sx={{ minWidth: 190 }}
      />

      <DebouncedTextField
        size="small"
        label="Search individuals..."
        value={filters.search}
        onCommit={(value) => set({ search: value })}
        sx={{ minWidth: 200 }}
      />

      {/* A window into the future over rollOffStartedDate: 0 … N days. Past roll-offs
          (negative "days until", i.e. recently-left rows) are excluded by the 0
          lower bound — use the Reason filter for those. */}
      <DebouncedTextField
        size="small"
        label="Rolling off within (days)"
        type="number"
        inputProps={{ min: 0 }}
        value={filters.daysUntilRollOffMax}
        onCommit={(value) => set({ daysUntilRollOffMax: value, daysUntilRollOffMin: value === '' ? '' : '0' })}
        sx={{ width: 190 }}
      />


      {/* Chips live on their own row so the group never breaks across the input row. */}
      <Box sx={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      {/* Status chips (multi-select). Selecting none falls back to the server default, New + Assigned. */}
      {STATUS_CHIPS.map((chip) => {
        const selected = toArray(filters.status) || [];
        const isSelected = selected.includes(chip.value);
        return (
          <FilterChip
            key={chip.value}
            label={chip.label}
            isSelected={isSelected}
            showDot
            backgroundColor={chip.backgroundColor}
            textColor={chip.textColor}
            borderColor={chip.borderColor}
            title={chip.title}
            onClick={() => {
              // At least one status stays selected: an empty selection would silently fall
              // back to the server default (New + Assigned) while every chip looks off.
              const next = isSelected ? selected.filter((value) => value !== chip.value) : [...selected, chip.value];
              if (next.length > 0) {
                set({ status: next });
              }
            }}
          />
        );
      })}

      {/* Independent eligibility toggle. */}
      <FilterChip
        label={ELIGIBLE_CHIP.label}
        isSelected={filters.eligible === 'true'}
        showDot
        backgroundColor={ELIGIBLE_CHIP.backgroundColor}
        textColor={ELIGIBLE_CHIP.textColor}
        borderColor={ELIGIBLE_CHIP.borderColor}
        title={ELIGIBLE_CHIP.title}
        onClick={() => set({ eligible: filters.eligible === 'true' ? '' : 'true' })}
      />
      </Box>
    </FilterBar>
  );
};

export default CandidatesFilterBar;
